import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Pencil, Pin } from "lucide-react";
import { toast } from "sonner";
import { drw, errText } from "./api";
import { PinControl } from "./GroupDialog";

/** Sheet index: number, title, revision, counts and flags. Tap to open; pencil to correct a
 *  title-block read. DRW-7 — once a set is grouped the same list reads as a tree, one node per
 *  group, pinned pages marked; there is no second page index. */
export default function SheetIndex({ sheets, activeId, onOpen, onChanged, search, setSearch, grouped = false }) {
  const [edit, setEdit] = useState(null);
  const [closed, setClosed] = useState(new Set());
  const save = async () => {
    try {
      await drw.patch(`/sheets/${edit.id}`, { sheet_number: edit.sheet_number, title: edit.title });
      setEdit(null); onChanged?.();
    } catch (e) { toast.error(errText(e)); }
  };
  const groups = useMemo(() => {
    if (!grouped) return [{ name: "", pages: sheets }];
    const by = new Map();
    sheets.forEach((s) => { const g = s.group || "Other"; if (!by.has(g)) by.set(g, []); by.get(g).push(s); });
    return [...by.entries()].map(([name, pages]) => ({ name, pages }));
  }, [sheets, grouped]);
  const names = groups.map((g) => g.name).filter(Boolean);
  const card = (s) => (
    <div key={s.id} className={`group rounded-lg border px-2.5 py-2 cursor-pointer transition-colors ${s.id === activeId ? "bg-orange-50 border-orange-300" : "bg-white hover:border-neutral-400"}`}
         onClick={() => onOpen(s.id)} data-testid={`sheet-card-${s.sheet_number}`} data-group={s.group || undefined} data-pinned={s.group_pinned ? "1" : undefined}>
      {edit?.id === s.id ? (
        <div className="space-y-1" onClick={(e) => e.stopPropagation()}>
          <input value={edit.sheet_number} onChange={(e) => setEdit({ ...edit, sheet_number: e.target.value })} className="w-full h-7 text-xs border rounded px-1 font-mono" data-testid="sheet-edit-number" />
          <input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="w-full h-7 text-xs border rounded px-1" data-testid="sheet-edit-title" />
          <div className="flex gap-1 justify-end"><button type="button" onClick={() => setEdit(null)} className="text-xs px-2 py-0.5 border rounded">Cancel</button><button type="button" onClick={save} className="text-xs px-2 py-0.5 bg-neutral-900 text-white rounded" data-testid="sheet-edit-save">Save</button></div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <span className="font-mono font-bold text-sm">{s.sheet_number}</span>
            <span className="text-[10px] px-1.5 rounded bg-neutral-100 text-neutral-600">Rev {s.revision_no}</span>
            {s.group_pinned && <Pin className="w-3 h-3 text-orange-700" data-testid={`pinned-${s.sheet_number}`} />}
            {!s.number_confident && <span className="text-[10px] px-1.5 rounded bg-amber-100 text-amber-800" title="Sheet number not read from the title block">check #</span>}
            <span className="ml-auto flex items-center gap-1">
              {grouped && <PinControl sheet={s} groups={names} onChanged={onChanged} />}
              <button type="button" onClick={(e) => { e.stopPropagation(); setEdit({ id: s.id, sheet_number: s.sheet_number, title: s.title }); }}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-neutral-100" aria-label="Correct sheet" data-testid={`sheet-edit-${s.sheet_number}`}><Pencil className="w-3 h-3" /></button>
            </span>
          </div>
          <div className="text-xs text-neutral-700 truncate">{s.title}</div>
          <div className="flex gap-1 mt-1 text-[10px]">
            {s.markups > 0 && <span className="px-1.5 rounded bg-neutral-100">{s.markups} markups</span>}
            {s.verify > 0 && <span className="px-1.5 rounded bg-orange-100 text-orange-800">{s.verify} verify</span>}
            {s.not_reissued && <span className="px-1.5 rounded bg-amber-100 text-amber-800">not reissued</span>}
            {s.changed && <span className="px-1.5 rounded bg-red-100 text-red-800">ASI</span>}
          </div>
        </>
      )}
    </div>
  );
  return (
    <div className="flex flex-col h-full" data-testid="sheet-index" data-grouped={grouped ? "1" : undefined}>
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Sheet, title or room…"
             className="h-9 text-sm border rounded-md px-2 mb-2" data-testid="sheet-search" />
      <div className="overflow-auto flex-1 space-y-1">
        {groups.map((g) => (
          <div key={g.name || "_all"} data-testid={g.name ? `sheet-group-${g.name}` : undefined}>
            {g.name && (
              <button type="button" onClick={() => setClosed((c) => { const n = new Set(c); n.has(g.name) ? n.delete(g.name) : n.add(g.name); return n; })}
                      className="w-full flex items-center gap-1 px-1 py-1 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 rounded" data-testid={`group-toggle-${g.name}`}>
                {closed.has(g.name) ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                <span className="truncate">{g.name}</span><span className="ml-auto font-normal text-neutral-500">{g.pages.length}</span>
              </button>
            )}
            {!closed.has(g.name) && <div className="space-y-1">{g.pages.map(card)}</div>}
          </div>
        ))}
        {sheets.length === 0 && <div className="text-xs text-neutral-400 p-2">No sheets match.</div>}
      </div>
    </div>
  );
}
